/**
 * Picks the party a record is about - a registered client OR a juhuklient.
 *
 * `ClientPicker` searches one table and is still the right control everywhere a
 * client is genuinely the only answer (a project, a contract, an invoice). This
 * one exists because two screens are no longer in that position: a support
 * ticket and a call are both routinely about somebody who has rung once and has
 * never been invoiced, and until this control existed the only way to file
 * either against that person was to promote them to a full client first.
 *
 * Both sides are queried in parallel and shown as two labelled groups rather
 * than one merged list, because "is this an existing customer or a lead" is
 * exactly the distinction the operator is making at that moment - a flat list
 * with a company name in it twice answers the question worse than no list.
 *
 * Never CREATES either record; everything here has to exist already. The
 * voicemail intake is what creates leads, and `NewClientModal` is what creates
 * clients.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Sprout, Users } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { getLeads } from "../../../lib/clients";
import { getClientOptions } from "../../../lib/projects";

/** Which table an id belongs to. The caller stores one column or the other. */
export type PartyKind = "client" | "lead";

export interface PartyRef {
  kind: PartyKind;
  id: string;
  name: string;
}

interface Group {
  kind: PartyKind;
  rows: { id: string; name: string }[];
}

/** Per side, so one crowded table cannot push the other off the panel. */
const PER_KIND = 8;

export function PartyPicker({
  value,
  selected,
  onChange,
  onPick,
  placeholder,
  required,
}: {
  /** What is in the box. The caller owns it, exactly like `ClientPicker`. */
  value: string;
  /** What is actually attached, or null while a name is only typed. */
  selected: PartyRef | null;
  onChange: (v: string) => void;
  onPick: (party: PartyRef) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const t = useT();
  const [groups, setGroups] = useState<Group[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Suppresses the query that would otherwise fire the moment a name is picked.
  const lastPicked = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 1 || (lastPicked.current !== null && value === lastPicked.current)) {
      setGroups([]);
      setOpen(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      // `allSettled`, not `all`: one side failing (a permission the operator
      // does not have, a slow query) must still show the other rather than
      // leaving the field looking like nothing matched.
      void Promise.allSettled([getClientOptions(q), getLeads({ q, pageSize: PER_KIND })])
        .then(([clients, leads]) => {
          if (!alive) return;
          const next: Group[] = [];
          if (clients.status === "fulfilled" && clients.value.rows.length > 0) {
            next.push({ kind: "client", rows: clients.value.rows.slice(0, PER_KIND) });
          }
          if (leads.status === "fulfilled" && leads.value.rows.length > 0) {
            next.push({
              kind: "lead",
              rows: leads.value.rows.slice(0, PER_KIND).map((l) => ({ id: l.id, name: l.name })),
            });
          }
          setGroups(next);
          setOpen(next.length > 0);
        })
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function handleType(v: string) {
    // Typed by hand, so allow a fresh search.
    lastPicked.current = null;
    onChange(v);
  }

  return (
    <div ref={boxRef} className="relative">
      <label className="crm-inp">
        <input
          value={value}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => groups.length > 0 && setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          // A name that was typed but never picked is not a party yet, and the
          // form behind this field saves an id, not a string.
          aria-invalid={required && !selected ? true : undefined}
        />
        {searching ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="size-4 shrink-0 text-muted-foreground" />
        )}
      </label>

      {open && groups.length > 0 ? (
        <ul className="absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-3xl bg-[var(--crm-surface)] p-1.5 shadow-[var(--crm-sh-lg)]">
          {groups.map((group) => (
            <li key={group.kind}>
              <div className="px-3.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t(group.kind === "client" ? "party.clients" : "party.leads")}
              </div>
              <ul>
                {group.rows.map((row) => (
                  <li key={`${group.kind}:${row.id}`}>
                    <button
                      type="button"
                      // Keep focus on the field until the click registers.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        lastPicked.current = row.name;
                        setOpen(false);
                        setGroups([]);
                        onChange(row.name);
                        onPick({ kind: group.kind, id: row.id, name: row.name });
                      }}
                      className="flex w-full items-center gap-2 rounded-2xl px-3.5 py-2.5 text-left hover:bg-[var(--crm-sunk)]"
                    >
                      {group.kind === "client" ? (
                        <Users className="size-3.5 shrink-0 text-muted-foreground" />
                      ) : (
                        <Sprout className="size-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="crm-prow-n crm-trunc min-w-0 flex-1">{row.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
